import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Users, Package, TruckIcon, CheckCircle2, AlertOctagon, Banknote, Wallet2, ShieldCheck, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import SurepassUsage from "@/components/admin/SurepassUsage";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "month", label: "This month" },
];

const Delta = ({ cur, prev }) => {
  if (prev === 0 && cur === 0) return <span className="text-xs text-slate-400 inline-flex items-center gap-0.5"><Minus className="w-3 h-3"/> no change</span>;
  if (prev === 0) return <span className="text-xs text-emerald-600 inline-flex items-center gap-0.5"><TrendingUp className="w-3 h-3"/> new</span>;
  const pct = Math.round(((cur - prev) / Math.max(prev, 1)) * 100);
  if (pct === 0) return <span className="text-xs text-slate-400 inline-flex items-center gap-0.5"><Minus className="w-3 h-3"/> flat</span>;
  const up = pct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`text-xs inline-flex items-center gap-0.5 ${up ? "text-emerald-600" : "text-red-600"}`}>
      <Icon className="w-3 h-3"/> {up ? "+" : ""}{pct}% vs prev
    </span>
  );
};

const Tile = ({ testid, title, value, prevValue, icon: Icon, tone = "slate", to, subtitle }) => {
  const tones = {
    slate: "bg-slate-900 text-white",
    amber: "bg-amber-500 text-white",
    emerald: "bg-emerald-600 text-white",
    blue: "bg-blue-600 text-white",
    red: "bg-red-600 text-white",
    violet: "bg-violet-600 text-white",
    green: "bg-green-600 text-white",
  };
  const inner = (
    <div className="p-4 h-full flex flex-col justify-between group">
      <div className="flex items-start justify-between gap-2">
        <div className={`w-9 h-9 rounded-lg grid place-items-center ${tones[tone]}`}>
          <Icon className="w-4 h-4"/>
        </div>
        {to && <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-900 transition"/>}
      </div>
      <div className="mt-3">
        <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
        {prevValue !== undefined && (
          <div className="mt-2"><Delta cur={Number(value) || 0} prev={Number(prevValue) || 0}/></div>
        )}
      </div>
    </div>
  );
  const cls = "rounded-2xl bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition h-full";
  return to
    ? <Link to={to} data-testid={testid} className={cls}>{inner}</Link>
    : <div data-testid={testid} className={cls}>{inner}</div>;
};

const Dashboard = () => {
  const [range, setRange] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { range };
      if (range === "custom") {
        if (!customStart || !customEnd) {
          setLoading(false);
          return;
        }
        params.start = new Date(customStart).toISOString();
        params.end = new Date(customEnd).toISOString();
      }
      const r = await adminApi.get("/summary", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (range !== "custom") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const cur = data?.current || {};
  const prev = data?.previous || {};
  const rangeParam = useMemo(() => {
    if (range === "custom" && customStart && customEnd) {
      return `range=custom&start=${encodeURIComponent(new Date(customStart).toISOString())}&end=${encodeURIComponent(new Date(customEnd).toISOString())}`;
    }
    return `range=${range}`;
  }, [range, customStart, customEnd]);

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Everything happening on the platform right now.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="range-selector">
          {RANGES.map(r => (
            <button
              key={r.key}
              data-testid={`range-${r.key}`}
              onClick={() => { setRange(r.key); setCustomOpen(false); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${range === r.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}
            >
              {r.label}
            </button>
          ))}
          <button
            data-testid="range-custom"
            onClick={() => { setRange("custom"); setCustomOpen(true); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${range === "custom" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            Custom
          </button>
        </div>
      </div>
      <SurepassUsage/>

      {customOpen && (
        <div className="mb-6 rounded-2xl bg-white border border-slate-200 p-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Start</label>
            <input data-testid="custom-start" type="datetime-local" value={customStart} onChange={e => setCustomStart(e.target.value)} className="mt-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm block"/>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">End</label>
            <input data-testid="custom-end" type="datetime-local" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="mt-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm block"/>
          </div>
          <Button data-testid="custom-apply" onClick={load} disabled={!customStart || !customEnd}>Apply</Button>
        </div>
      )}

      {loading && !data && <div className="text-sm text-slate-500">Loading…</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Tile
              testid="tile-kyc-pending"
              title="Carriers awaiting KYC"
              value={data.kyc.pending_submitted}
              icon={ShieldCheck}
              tone="amber"
              to="/admin/kyc"
              subtitle={`+${data.kyc.never_submitted} never submitted`}
            />
            <Tile
              testid="tile-users"
              title="Total users"
              value={data.users.total}
              icon={Users}
              tone="slate"
              to="/admin/users"
              subtitle={`${data.users.senders} senders · ${data.users.carriers} carriers`}
            />
            <Tile
              testid="tile-disputes"
              title="Open disputes"
              value={data.disputes.open}
              icon={AlertOctagon}
              tone="red"
              to="/admin/disputes"
            />
            <Tile
              testid="tile-payouts"
              title="Carriers awaiting payout"
              value={data.payouts.carriers_awaiting}
              icon={Banknote}
              tone="violet"
              to="/admin/payouts"
              subtitle={`₹${(data.payouts.total_owed || 0).toLocaleString()} owed`}
            />
          </div>

          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">For selected period</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Tile
              testid="tile-parcels-posted"
              title="Parcels posted"
              value={cur.parcels_posted}
              prevValue={prev.parcels_posted}
              icon={Package}
              tone="blue"
              to={`/admin/parcels?${rangeParam}`}
            />
            <Tile
              testid="tile-in-transit"
              title="In transit right now"
              value={data.live.in_transit}
              icon={TruckIcon}
              tone="emerald"
              to="/admin/parcels?status=in_transit"
              subtitle="Live figure, not windowed"
            />
            <Tile
              testid="tile-delivered"
              title="Delivered in window"
              value={cur.delivered}
              prevValue={prev.delivered}
              icon={CheckCircle2}
              tone="green"
              to={`/admin/parcels?status=delivered&${rangeParam}`}
            />
            <Tile
              testid="tile-commission"
              title="Commission earned"
              value={`₹${(cur.commission || 0).toLocaleString()}`}
              prevValue={prev.commission}
              icon={Wallet2}
              tone="slate"
              subtitle={`Prev: ₹${(prev.commission || 0).toLocaleString()}`}
            />
          </div>

          <div className="text-xs text-slate-400" data-testid="range-window-label">
            Showing {new Date(data.range.start).toLocaleString()} → {new Date(data.range.end).toLocaleString()}
            {" · "}Prev: {new Date(data.range.prev_start).toLocaleString()} → {new Date(data.range.prev_end).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
